import React, { useState, useEffect, useMemo } from "react";
import { ref, onValue, update } from "firebase/database";
import { db } from "../../assets/firebase/firebaseconfig";
import "./style.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Loading from "../Loading";
import { createSlug } from "../../assets/js/slug"; // Import hàm tạo slug

const ProjectsPage = () => {
  const [projectsData, setProjectsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Tất cả dự án");
  const [activeRegion, setActiveRegion] = useState("Tất cả khu vực");
  const [sortBy, setSortBy] = useState("all");
  const [visibleCount, setVisibleCount] = useState(10);

  // 1. Lấy dữ liệu Realtime và Tự động Update Slug
  useEffect(() => {
    const projectsRef = ref(db, "projects");
    const unsubscribe = onValue(projectsRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const updates = {};
        let hasMissingSlug = false;

        const list = Object.keys(data).map((key) => {
          // Nếu dự án chưa có slug, tạo ngay từ tiêu đề dự án
          if (!data[key].slug && data[key].title) {
            const newSlug = createSlug(data[key].title);
            updates[`/projects/${key}/slug`] = newSlug;
            hasMissingSlug = true;
          }
          return {
            id: key,
            ...data[key],
          };
        });

        // Đẩy slug mới lên Firebase một lần duy nhất
        if (hasMissingSlug) {
          await update(ref(db), updates);
          console.log("✅ Đã cập nhật slug cho danh sách dự án!");
        }

        setProjectsData(list.reverse()); // Mới nhất lên đầu
      } else {
        setProjectsData([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Logic Lọc dự án
  const filteredProjects = useMemo(() => {
    return projectsData.filter((p) => {
      const matchCat = activeCategory === "Tất cả dự án" || p.category === activeCategory;
      const matchRegion = activeRegion === "Tất cả khu vực" || p.region === activeRegion;

      let matchStatus = true;
      if (sortBy === "Đang thực hiện") matchStatus = p.status === "Đang thi công" || p.status === "Đang thực hiện";
      if (sortBy === "Đã hoàn thành") matchStatus = p.status === "Đã hoàn thành";

      return matchCat && matchRegion && matchStatus;
    });
  }, [projectsData, activeCategory, activeRegion, sortBy]);

  const displayedProjects = filteredProjects.slice(0, visibleCount);

  // 3. Danh mục & Khu vực
  const categories = ["Tất cả dự án", "Lắp đặt điều hoà trung tâm", "Lắp đặt điều hòa treo tường", "Hàn cơ khí", "Xây dựng", "Lắp đặt hệ thống điện nước", "Lắp đặt hệ thống năng lượng mặt trời", "Lắp đặt hệ thống thông gió"];
  const regions = ["Tất cả khu vực", "TP Hà Nội", "TP HCM", "Nha Trang", "Nam Định", "Lào Cai", "Thái Nguyên", "Đà Nẵng", "Nghệ An", "Quảng Ninh"];

  const getStatusStyle = (status) => {
    switch (status) {
      case "Đã hoàn thành": return { backgroundColor: "#267326", color: "#fff" };
      case "Đang thi công":
      case "Đang thực hiện": return { backgroundColor: "#f5a623", color: "#fff" };
      case "Tạm dừng": return { backgroundColor: "#d0021b", color: "#fff" };
      default: return { backgroundColor: "#9b9b9b", color: "#fff" };
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeCategory, activeRegion, sortBy]);

  if (loading) return <Loading />;

  return (
    <div className="shopee-page-bg" style={{ padding: "10rem 4rem" }}>
      <Helmet>
        <title>Dự Án Tiêu Biểu - Năng Lực Thi Công | Habako M&E</title>
        <meta name="description" content="Khám phá các dự án cơ điện, lắp đặt điều hòa trung tâm tiêu biểu mà Habako đã thực hiện thành công trên toàn quốc." />
      </Helmet>

      <div className="shopee-layout">
        <aside className="shopee-sidebar">
          <div className="sidebar-header"><i className="fas fa-filter"></i> BỘ LỌC TÌM KIẾM</div>
          
          <div className="sidebar-section">
            <h3 className="sidebar-section__title">Theo Chuyên Mục</h3>
            <ul className="sidebar-list">
              {categories.map((cat, idx) => (
                <li key={idx} className={`sidebar-item ${activeCategory === cat ? "active" : ""}`} onClick={() => setActiveCategory(cat)}>
                  {cat}
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-divider"></div>

          <div className="sidebar-section">
            <h3 className="sidebar-section__title">Theo Khu Vực</h3>
            <ul className="sidebar-list">
              {regions.map((reg, idx) => (
                <li key={idx} className={`sidebar-item ${activeRegion === reg ? "active" : ""}`} onClick={() => setActiveRegion(reg)}>
                  {reg}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="shopee-main">
          <div className="shopee-sort-bar">
            <span>Sắp xếp theo: </span>
            {["all", "Đang thực hiện", "Đã hoàn thành"].map((type) => (
              <button key={type} className={`shopee-sort-option ${sortBy === type ? "active" : ""}`} onClick={() => setSortBy(type)}>
                {type === "all" ? "Tất cả" : type}
              </button>
            ))}
          </div>

          <div className="shopee-product-grid">
            {displayedProjects.map((project) => (
              <div key={project.id} className="shopee-product-item">
                {/* SỬA TẠI ĐÂY: Link dùng slug thay vì id */}
                <Link to={`/du-an/${project.slug || createSlug(project.title)}`}>
                  <div className="shopee-product-item__img-wrapper">
                    <img src={project.image} alt={project.title} />
                    <div className="shopee-product-item__badge">{project.region}</div>
                    <div className="project-status-badge" style={getStatusStyle(project.status)}>
                      {project.status}
                    </div>
                  </div>
                  <div className="shopee-product-item__info">
                    <h3 className="shopee-product-item__name">{project.title}</h3>
                    <div className="shopee-product-item__category-tag">{project.category}</div>
                    <div className="shopee-product-item__footer">
                      <span className="shopee-product-item__sold">
                        Năng lực thi công Habako M&E
                      </span>
                    </div>
                  </div>
                  <div className="shopee-product-item__hover-footer">Chi tiết dự án</div>
                </Link>
              </div>
            ))}
          </div>

          {visibleCount < filteredProjects.length && (
            <div className="show-more-container">
              <button className="btn-show-more-custom" onClick={() => setVisibleCount(v => v + 10)}>
                Xem thêm {filteredProjects.length - visibleCount} dự án
              </button>
            </div>
          )}

          {filteredProjects.length === 0 && (
            <div className="shopee-empty-state">
              <p>Hiện tại chưa có dự án nào thuộc mục này.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProjectsPage;